import { ActionOption } from './review-actions';
import { ReviewKind } from './review-visibility';

/**
 * What the reviewer is asked (plan §6). Each prompt is the *user message* of an ordinary chat turn:
 * the Query Tool keeps its system prompt byte-identical for prompt caching, so per-request
 * instructions belong here. The headings are fixed because `parseReview` reads them.
 */

/** Bump when a prompt changes, so a stored review can be told from one made with an older prompt. */
export const REVIEW_PROMPT_VERSION = 1;

/** The first line of every review message; the thread shows only what follows it on that line. */
export const REVIEW_MESSAGE_MARKER = `[CENTERLINE document review v${REVIEW_PROMPT_VERSION}]`;

/** Run options for a review (plan §4.5), in one place. The model is the Query Tool's default. */
export const REVIEW_RUN_OPTIONS = { effort: 'high', steps: '40' };
export const SUBMITTAL_TOOL_NUDGE = 'search_specifications_text';

/** Longest total length of the document's own text fields in the prompt. */
export const MAX_DOCUMENT_TEXT = 6000;

export const SUBMITTAL_HEADINGS = {
  summary: 'Summary',
  doesNotComply: 'Does not comply',
  missing: 'Missing or unclear',
  complies: 'Complies',
  substitutions: 'Substitutions',
  coordination: 'Coordination',
  action: 'Recommended action',
  response: 'Proposed response to contractor',
} as const;

export const RFI_HEADINGS = {
  question: 'Question',
  answer: 'Answer',
  basis: 'Basis in the contract documents',
  impact: 'Impact',
  open: 'Open questions',
  response: 'Proposed response to contractor',
} as const;

/** The document's own facts, so the model does not have to look them up. */
export interface ReviewDocumentFacts {
  /** "Submittal 08 71 00-2", "RFI 014": how the document is named on the page. */
  label: string;
  title?: string | null;
  /** The specification section a submittal is for, e.g. "08 71 00". */
  section?: string | null;
  paragraph?: string | null;
  proposedSubstitution?: string | null;
  description?: string | null;
  request?: string | null;
}

export interface ReviewPromptFiles {
  /** Names of the files the review can read. */
  included: string[];
  /** Names of the document's files that are not ready yet, so the model does not assume them. */
  notIncluded: string[];
}

const clip = (text: string | null | undefined, room: number): string => {
  const t = (text ?? '').trim();
  return t.length > room ? `${t.slice(0, Math.max(0, room - 1))}…` : t;
};

/** Facts as lines, with the free-text fields sharing `MAX_DOCUMENT_TEXT` between them. */
function factLines(facts: ReviewDocumentFacts): string[] {
  let room = MAX_DOCUMENT_TEXT;
  const take = (text: string | null | undefined): string => {
    const t = clip(text, room);
    room -= t.length;
    return t;
  };
  const lines: string[] = [`Document: ${facts.label}`];
  const add = (name: string, value: string) => {
    if (value) lines.push(`${name}: ${value}`);
  };
  add('Title', take(facts.title));
  add('Specification section', (facts.section ?? '').trim());
  add('Paragraph', (facts.paragraph ?? '').trim());
  add('Proposed substitution', take(facts.proposedSubstitution));
  add('Description', take(facts.description));
  add('Request', take(facts.request));
  return lines;
}

function fileLines(files: ReviewPromptFiles): string[] {
  const lines = [
    files.included.length
      ? `Files you can read (search_text is limited to these): ${files.included.join('; ')}`
      : 'This document has no files you can read.',
  ];
  if (files.notIncluded.length) {
    lines.push(
      `Not available yet (still being processed, so do not assume anything about them): ${files.notIncluded.join(
        '; ',
      )}`,
    );
  }
  return lines;
}

const headingList = (headings: Record<string, string>): string =>
  Object.values(headings)
    .map((h, i) => `${i + 1}. \`## ${h}\``)
    .join('\n');

const SHARED_RULES = [
  'Attach a snip (crop) of any drawing detail or page you rely on.',
  'Keep the headings exactly as written, in this order, so they can be read by software.',
  'Say when you are unsure. Cite everything; never invent a requirement.',
].join(' ');

/** The shortcut text shown in the thread for a review, whatever the instructions say. */
export const reviewDisplayMessage = (kind: ReviewKind, facts: ReviewDocumentFacts): string =>
  kind === 'submittal'
    ? `Review ${facts.label} against the specifications and drawings`
    : `Answer ${facts.label} from the contract documents`;

export function buildSubmittalReviewMessage(
  facts: ReviewDocumentFacts,
  files: ReviewPromptFiles,
  actions: ActionOption[],
): string {
  const actionWords = actions.length
    ? actions.map((a) => a.label).join(', ')
    : 'No Exceptions Taken, Amend As Noted, Revise & Resubmit, Rejected, Submit Specified Item';
  return [
    `${REVIEW_MESSAGE_MARKER} ${reviewDisplayMessage('submittal', facts)}`,
    '',
    'You are assisting an Architect with a submittal review for general conformance with the design intent and the contract documents. You are not a consultant and you do not approve anything.',
    '',
    ...factLines(facts),
    ...fileLines(files),
    '',
    'Method:',
    '1. Find the governing specification section(s) with search_specifications_text, using section_number set to the submittal\'s section. Check Part 1, the "Submittals" article (article_kind: submittals), for what must be submitted. Check Part 2, Products, for listed manufacturers and products and the substitution rules; materials, performance and ratings (fire rating, UL listing, ASTM standards, STC, U-value, wind or seismic); sizes, finishes and colors; accessories and warranties. Check Part 3 where shop drawings must show installation.',
    "2. Read the submittal's own files (search_text) for each requirement.",
    '3. Check the Drawings where the submittal must fit: schedules (door, hardware, equipment, finish), details and dimensions, quantities and locations, using list_drawing_sheets, search_drawing_text and trace_drawing_xrefs.',
    '4. When a drawing detail or a page of the submittal shows a finding, call crop_drawing_region or crop_page_region so the reviewer sees the snip.',
    '',
    'Answer with these headings, in this order:',
    headingList(SUBMITTAL_HEADINGS),
    '',
    `- \`## ${SUBMITTAL_HEADINGS.summary}\`: two or three sentences, including the governing section(s).`,
    `- \`## ${SUBMITTAL_HEADINGS.doesNotComply}\`: each item with the requirement (cite the specification clause or sheet) and what was submitted (cite the submittal page).`,
    `- \`## ${SUBMITTAL_HEADINGS.missing}\`: required items not submitted (from the Submittals article: product data, shop drawings, samples, certificates, test reports, warranty, calculations stamped by a licensed engineer for delegated design, sustainability documentation) and anything that cannot be verified.`,
    `- \`## ${SUBMITTAL_HEADINGS.complies}\`: the requirements met, briefly, with citations.`,
    `- \`## ${SUBMITTAL_HEADINGS.substitutions}\`: products that are not the specified or listed ones, and whether the substitution procedures were followed.`,
    `- \`## ${SUBMITTAL_HEADINGS.coordination}\`: conflicts with the drawings or other trades, and dimensions to be field-verified.`,
    `- \`## ${SUBMITTAL_HEADINGS.action}\`: exactly one of ${actionWords}, in those words, then one sentence of why.`,
    `- \`## ${SUBMITTAL_HEADINGS.response}\`: the comments as the Architect would send them: professional, numbered, each citing the specification clause or sheet. Do not mention AI.`,
    '',
    "Rules: If the specification section cannot be found, say so rather than guessing. Do not comment on means and methods, quantities or field dimensions beyond noting they are the contractor's responsibility.",
    SHARED_RULES,
  ].join('\n');
}

export function buildRfiAnswerMessage(
  facts: ReviewDocumentFacts,
  files: ReviewPromptFiles,
): string {
  return [
    `${REVIEW_MESSAGE_MARKER} ${reviewDisplayMessage('rfi', facts)}`,
    '',
    "You are helping an Architect answer a contractor's RFI from the contract documents.",
    '',
    ...factLines(facts),
    ...fileLines(files),
    '',
    'Method:',
    '1. Restate the question.',
    "2. Find where the documents address it: the drawings (sheets, details, schedules, cross-references), the specifications (the clause), and the RFI's own attachments.",
    '3. Use the pixel tools (crop_drawing_region, crop_page_region) for the key detail.',
    '',
    'Answer with these headings, in this order:',
    headingList(RFI_HEADINGS),
    '',
    `- \`## ${RFI_HEADINGS.question}\`: the question, restated.`,
    `- \`## ${RFI_HEADINGS.answer}\`: direct, citing sheets and specification clauses. If the documents already answer it, say so and point to where.`,
    `- \`## ${RFI_HEADINGS.basis}\`: the sheets and clauses it rests on.`,
    `- \`## ${RFI_HEADINGS.impact}\`: whether the answer changes scope, cost or time and may need a change event or supplemental instruction; whether it is really a substitution request; whether a consultant (for example structural or MEP) should confirm.`,
    `- \`## ${RFI_HEADINGS.open}\`: anything the documents do not settle.`,
    `- \`## ${RFI_HEADINGS.response}\`: what the Architect would send, with numbered references. Do not mention AI.`,
    '',
    'Rules: Do not make design decisions the documents do not support; flag them for the Architect instead.',
    SHARED_RULES,
  ].join('\n');
}

/**
 * A stored review message split for display: the label the thread shows and the instructions that
 * were sent (kept behind a disclosure). Null for a message the user typed.
 */
export function splitReviewMessage(
  question: string,
): { label: string; instructions: string } | null {
  if (!question.startsWith('[CENTERLINE document review')) return null;
  const end = question.indexOf('\n');
  const first = end < 0 ? question : question.slice(0, end);
  const close = first.indexOf(']');
  if (close < 0) return null;
  return {
    label: first.slice(close + 1).trim() || 'Document review',
    instructions: end < 0 ? '' : question.slice(end + 1).trim(),
  };
}
